import {
  BodyText,
  Checkbox,
  Flex,
  Heading,
  InlineLink,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';
import { useEffect } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { MarketingConsent } from '@components/MarketingConsent';
import { ReferralField } from '@components/ReferralField';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { formatConnectorsOptions } from '@features/Partner/utils';
import {
  AttributionDetails,
  BundleQuoteFragment,
  EnergyQuoteType,
  PartnerConnector,
} from '@generated/gql/graphql';
import { useTelcoLegal } from '@hooks/useTelcoLegal';
import { BooleanSelection } from '@src/types/index';
import { YourDetailsFormValues as FormValues } from '@src/types/yourDetails';

import './styles.scss';

interface ReferralLegalProps {
  connectors: Nullable<PartnerConnector[]>;
  attributionDetails: Nullable<AttributionDetails>;
  isSwitch: boolean;
  isFixedSelected: boolean;
  selectedQuotes: BundleQuoteFragment[];
}

const getSelectedServicesQuantity = (selectedQuotes: BundleQuoteFragment[]) => {
  const uniqueProductTypes = new Set(
    selectedQuotes.map((quote) => quote.product.type)
  );

  return uniqueProductTypes.size;
};

const getCoolingOffPeriod = (selectedQuotes: BundleQuoteFragment[]) =>
  getSelectedServicesQuantity(selectedQuotes) > 1 ? 30 : 14;

const showReferralField = (
  attributionDetails: Nullable<AttributionDetails>
) => {
  if (!attributionDetails) return true;

  const { assistant, referrer } = attributionDetails;

  if (!referrer) {
    return true;
  }

  if (assistant?.sales && referrer?.sales) {
    return assistant.sales.oktaUsername === referrer.sales.oktaUsername;
  }

  if (assistant?.partner && referrer?.partner) {
    return assistant.partner.positionId === referrer.partner.positionId;
  }

  return false;
};

const getReferralFieldLabel = (
  attributionDetails: Nullable<AttributionDetails>,
  hasConnector: boolean
) => {
  const hasAssistant = !!attributionDetails?.assistant;

  if (!hasAssistant) {
    return 'Finally, did you find out about us from another UW customer or Partner?';
  }

  if (hasConnector) {
    return 'Finally, did you find out about us from another UW customer or UW connector?';
  }

  return 'Finally, did you find out about us from another UW customer?';
};

const ReferralLegal = ({
  connectors,
  attributionDetails,
  isSwitch,
  selectedQuotes,
  isFixedSelected,
}: ReferralLegalProps) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    initialValues,
  }: FormikProps<FormValues> = useFormikContext();

  const hasTrackerTariff = selectedQuotes.find(
    (quote) =>
      quote.product.details?.__typename === 'EnergyProductDetails' &&
      quote.product.details?.quoteType === EnergyQuoteType.Tracker
  );

  const { hasTelcoService, generateContract, downloadContract } = useTelcoLegal(
    selectedQuotes,
    values
  );

  const marketingConsentDialog = useDialog(DialogID.MARKETING_CONSENT);

  useEffect(() => {
    if (!selectedQuotes.length) return;

    generateContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuotes.length]);

  useEffect(() => {
    setFieldValue('rlTermsAccept', BooleanSelection.No);
  }, [isSwitch, setFieldValue]);

  const isPartnerAssistant = !!attributionDetails?.assistant?.partner;
  const hasConnector = !!(connectors && connectors.length > 0);

  return (
    <>
      <Flex direction="column" spacing="xl">
        <Flex direction="column" spacing="lg">
          <Heading as="h3" size="sm">
            Cooling off period
          </Heading>
          <BodyText>
            You can cancel for any reason within{' '}
            {getCoolingOffPeriod(selectedQuotes)} days of submitting your order.
            See our{' '}
            <InlineLink
              href={EXTERNAL_LINKS.termsAndConditions}
              target="_blank"
              rel="noreferrer noopener"
              hideOpenIcon
            >
              terms
            </InlineLink>{' '}
            for more details.
          </BodyText>
        </Flex>

        <Flex direction="column" spacing="lg">
          <Heading as="h3" size="sm">
            Terms and Conditions
          </Heading>

          <Checkbox
            data-testid="rlTermsAccept"
            id="rlTermsAccept"
            className="referralLegal__acceptTerms"
            name="rlTermsAccept"
            checked={values.rlTermsAccept === BooleanSelection.Yes}
            onCheckedChange={(checked) => {
              setFieldValue(
                'rlTermsAccept',
                checked ? BooleanSelection.Yes : BooleanSelection.No
              );
            }}
            label={
              <BodyText as="span">
                I have read and understood the{' '}
                <InlineLink
                  href={EXTERNAL_LINKS.termsAndConditions}
                  target="_blank"
                  rel="noreferrer noopener"
                  hideOpenIcon
                >
                  Terms and Conditions
                </InlineLink>
                {hasTelcoService && (
                  <>
                    <span>, </span>
                    <InlineLink
                      href={EXTERNAL_LINKS.tariffInformation}
                      target="_blank"
                      rel="noreferrer noopener"
                      hideOpenIcon
                    >
                      Tariff Information
                    </InlineLink>
                  </>
                )}
                <span> and the </span>
                <InlineLink
                  href={EXTERNAL_LINKS.privacyPolicy}
                  target="_blank"
                  rel="noreferrer noopener"
                  hideOpenIcon
                >
                  Privacy Notice
                </InlineLink>
                {hasTelcoService && (
                  <>
                    <span>, as well as the </span>
                    <InlineLink
                      onClick={(event) => {
                        event.preventDefault();
                        downloadContract();
                      }}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="referralLegal__telcoContractLink"
                      data-testid="telcoContractLink"
                      hideOpenIcon
                    >
                      Broadband and Mobile Contract Summary
                    </InlineLink>
                  </>
                )}
                <span>. </span>
                {isSwitch && (
                  <span>
                    I understand what will happen when I switch my Broadband
                    services to UW and provide my express consent to
                    continue.{' '}
                  </span>
                )}
                By submitting my order, I confirm that I accept the Terms and
                Conditions and that I have an obligation to pay for the services
                ordered. I want my services to start as soon as possible and I
                agree to pay for the services I use if I later cancel early.{' '}
                {isFixedSelected && (
                  <span>
                    I acknowledge and agree that my fixed energy tariff is based
                    on my chosen multi-service bundle and if I cancel any
                    non-energy services, I will be transferred to the cheapest
                    available fixed energy tariff of the same version number for
                    the remainder of my fixed term.
                  </span>
                )}
                {hasTrackerTariff && (
                  <span>
                    I acknowledge and agree that my Tracker energy tariff will
                    track the Government’s price cap at a set discount, so the
                    prices I pay during the Tracker term may change when the
                    price cap is updated. I acknowledge and agree that my
                    Tracker energy tariff is based on my chosen multi-service
                    bundle and if I cancel any non-energy services, I will be
                    transferred to the cheapest available Tracker energy tariff
                    of the same version number for the remainder of my Tracker
                    term.
                  </span>
                )}
              </BodyText>
            }
          />
          {Boolean(errors.rlTermsAccept) && touched.rlTermsAccept && (
            <ValidationText status="invalid">
              {errors.rlTermsAccept}
            </ValidationText>
          )}
        </Flex>
        {showReferralField(attributionDetails) && (
          <ReferralField
            captureConnectorReferral={hasConnector && isPartnerAssistant}
            label={getReferralFieldLabel(attributionDetails, hasConnector)}
            capturePartnerReferral={!attributionDetails?.assistant}
            connectorsOptions={
              connectors && formatConnectorsOptions(connectors)
            }
          />
        )}

        <BodyText className="referralLegal__mediaPrefsMessage">
          {values.rlAcceptMarketing !== initialValues.rlAcceptMarketing ? (
            <ValidationText status="valid">
              We've saved your contact preferences
            </ValidationText>
          ) : null}
        </BodyText>

        <BodyText>
          {values.rlAcceptMarketing === BooleanSelection.No && (
            <span>If you'd like to opt back in</span>
          )}
          {values.rlAcceptMarketing === BooleanSelection.Yes && (
            <span>
              We’ll keep in touch about how you could save even more time and
              money on your bills, as well as other relevant updates. You can
              opt out at any time, but if you’d rather not hear from us
            </span>
          )}
          , please{' '}
          <InlineLink onClick={() => marketingConsentDialog.setOpen(true)}>
            let us know
          </InlineLink>
          . Learn more about how we use your data in our{' '}
          <InlineLink
            href={EXTERNAL_LINKS.privacyPolicy}
            target="_blank"
            rel="noreferrer noopener"
            hideOpenIcon
          >
            Privacy Notice
          </InlineLink>
          .
        </BodyText>
      </Flex>
      <MarketingConsent
        content="If you choose not to hear from us, we won't be able to let you know how you could save more."
        agreeLabel="Keep up to date on new ways to save"
        declineLabel="Opt me out - I don't mind missing out on savings"
        name="rlAcceptMarketing"
        onClose={() => marketingConsentDialog.setOpen(false)}
      />
    </>
  );
};

export { ReferralLegal };
